import countryCodeList from '@/JsonFiles/CountryCodeList.json'
import {
  normalizeKnowledgeBaseFiles,
} from '@/mixins/Organization/UploadKnowledgeBase.js'

export const DEFAULT_COUNTRY_CODE = 'IN'

export const MOBILE_VALIDATION = {
  IN: {
    length: 10,
    pattern: /^\d{10}$/,
    hint: '10-digit mobile number',
    message: 'Enter a valid 10-digit number',
  },
  US: {
    length: 10,
    pattern: /^\d{10}$/,
    hint: '10-digit mobile number',
    message: 'Enter a valid 10-digit US mobile number',
  },
  CA: {
    length: 10,
    pattern: /^\d{10}$/,
    hint: '10-digit mobile number',
    message: 'Enter a valid 10-digit Canadian mobile number',
  },
  GB: {
    length: 10,
    pattern: /^\d{10}$/,
    hint: '10-digit mobile number',
    message: 'Enter a valid 10-digit UK mobile number',
  },
  AU: {
    length: 9,
    pattern: /^\d{9}$/,
    hint: '9-digit mobile number',
    message: 'Enter a valid 9-digit Australian mobile number',
  },
}

export const DEFAULT_MOBILE_VALIDATION = {
  min: 6,
  max: 15,
  pattern: /^\d{6,15}$/,
  hint: '6–15 digit mobile number',
  message: 'Enter a valid mobile number (6–15 digits)',
}

export default {
  data() {
    return {
      selectedCountryCode: DEFAULT_COUNTRY_CODE,
      isPopulatingForm: false,
      countryList: countryCodeList,
      countryRules: [(v) => !!v || 'Country code is required'],
      emailRules: [
        (v) => !!v?.trim() || 'Email is required',
        (v) => /.+@.+\..+/.test(v) || 'Enter a valid email',
      ],
      sipRules: [(v) => !!v?.trim() || 'SIP number is required'],
    }
  },
  computed: {
    countryOptions() {
      return this.countryList.map((country) => ({
        ...country,
        label: `${country.name} (${country.dial_code})`,
      }))
    },
    selectedCountry() {
      if (!this.selectedCountryCode) return null
      return (
        this.countryList.find((c) => c.code === this.selectedCountryCode) || null
      )
    },
    mobileValidation() {
      if (!this.selectedCountryCode) return DEFAULT_MOBILE_VALIDATION
      return (
        MOBILE_VALIDATION[this.selectedCountryCode] || DEFAULT_MOBILE_VALIDATION
      )
    },
    mobileMaxLength() {
      return this.mobileValidation.length || this.mobileValidation.max || 15
    },
    mobileHint() {
      return this.mobileValidation.hint
    },
    nameRules() {
      return [(v) => !!v?.trim() || 'Organisation name is required']
    },
    cityRules() {
      return [(v) => !!v?.trim() || 'City is required']
    },
    stateRules() {
      return [(v) => !!v?.trim() || 'State is required']
    },
    addressRules() {
      return [(v) => !!v?.trim() || 'Address is required']
    },
    manualKnowledgeBaseRules() {
      return [(v) => !!v?.trim() || 'Knowledge base is required']
    },
    uploadKnowledgeBaseRules() {
      return [
        (v) =>
          normalizeKnowledgeBaseFiles(v).length > 0 ||
          'Please select at least one file',
        (v) => {
          for (const file of normalizeKnowledgeBaseFiles(v)) {
            const name = file?.name?.toLowerCase() || ''
            if (!name.endsWith('.txt') && !name.endsWith('.pdf')) {
              return 'Only .txt and .pdf files are allowed'
            }
          }
          return true
        },
      ]
    },
    pincodeRules() {
      if (this.selectedCountryCode === 'IN') {
        return [
          (v) => !!String(v ?? '').trim() || 'Pincode is required',
          (v) =>
            /^\d{6}$/.test(String(v).trim()) || 'Enter a valid 6-digit pincode',
        ]
      }
      return [(v) => !!String(v ?? '').trim() || 'Pincode is required']
    },
    mobileRules() {
      const config = this.mobileValidation
      return [
        (v) => !!this.selectedCountryCode || 'Country code is required',
        (v) => !!String(v ?? '').trim() || 'Mobile number is required',
        (v) => /^\d+$/.test(String(v).trim()) || 'Digits only',
        (v) => {
          const value = String(v).trim()
          if (config.pattern && !config.pattern.test(value)) {
            return config.message
          }
          if (config.length && value.length !== config.length) {
            return `Must be exactly ${config.length} digits`
          }
          if (config.min && value.length < config.min) {
            return `Must be at least ${config.min} digits`
          }
          if (config.max && value.length > config.max) {
            return `Must be at most ${config.max} digits`
          }
          return true
        },
      ]
    },
  },
  methods: {
    normalizeDialCode(dialCode) {
      if (!dialCode) return ''
      return dialCode.replace(/\D/g, '')
    },
    resolveCountryCode(stored) {
      if (!stored) return DEFAULT_COUNTRY_CODE
      const raw = String(stored).trim().toUpperCase()
      const byCode = this.countryList.find((c) => c.code === raw)
      if (byCode) return byCode.code

      const normalized = raw.replace(/\D/g, '')
      const byDial = this.countryList.find(
        (c) => this.normalizeDialCode(c.dial_code) === normalized,
      )
      return byDial?.code || DEFAULT_COUNTRY_CODE
    },
    syncCountryCodeToForm() {
      if (this.form) {
        this.form.org_country_code = this.normalizeDialCode(
          this.selectedCountry?.dial_code,
        )
      }
    },
    allowDigitsOnly(event) {
      if (!/\d/.test(event.key)) event.preventDefault()
    },
    onCountryCodeChange(newVal, oldVal) {
      this.syncCountryCodeToForm()
      if (
        !this.isPopulatingForm &&
        oldVal != null &&
        newVal !== oldVal &&
        this.form
      ) {
        this.form.org_contact_number = ''
      }
    },
    resetCountryToDefault() {
      this.selectedCountryCode = DEFAULT_COUNTRY_CODE
      this.syncCountryCodeToForm()
    },
  },
}
